/**
 * Product Form Web Component & Universal AJAX Add to Cart
 * Handles variant add-to-cart, tier quantities, live loading spinner,
 * accessible aria-live error messages, Section Rendering updates, and drawer opening.
 */

window.themeBus = window.themeBus || {
  events: {},
  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
    return () => {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    };
  },
  publish(event, data) {
    if (this.events[event]) {
      this.events[event].forEach(cb => {
        try { cb(data); } catch (e) { console.error('themeBus listener error:', e); }
      });
    }
  }
};
class ProductForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.submitButton = this.querySelector('[type="submit"]');
    this.errorContainer = this.querySelector('.product-form__error-message-wrapper');

    if (!this.form._productFormBound) {
      this.form._productFormBound = true;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();
    if (this.submitButton) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
    }

    // Read active quantity from tiers or input
    const qInput = this.form.querySelector('input[name="quantity"]');
    const selectedTierRadio = document.querySelector('input[name^="quantity-tier"]:checked');
    const activeTier = document.querySelector('.qty-breaks__item.is-selected');
    let targetQuantity = 1;

    if (selectedTierRadio && selectedTierRadio.value) {
      targetQuantity = parseInt(selectedTierRadio.value, 10) || 1;
    } else if (activeTier && activeTier.dataset.tierQty) {
      targetQuantity = parseInt(activeTier.dataset.tierQty, 10) || 1;
    } else if (qInput) {
      targetQuantity = parseInt(qInput.value, 10) || 1;
    }

    if (qInput) {
      qInput.value = targetQuantity;
    }

    const formData = new FormData(this.form);
    formData.set('quantity', targetQuantity);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status && response.status >= 400) {
          const errorMsg = response.description || response.message || (window.cartStrings && window.cartStrings.error) || 'Item could not be added.';
          this.handleErrorMessage(errorMsg);
          return;
        }

        // Render updated cart drawer
        if (response.sections && window.themeCart && typeof window.themeCart.render === 'function') {
          window.themeCart.render(response.sections);
        } else if (response.sections && response.sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(response.sections['cart-drawer'], 'text/html');
          const newInner = parsed.querySelector('.cart-drawer__inner');
          const currentInner = document.querySelector('.cart-drawer__inner');
          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
          }
        }

        // Refresh bubbles and reward bar progress with latest cart data
        fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            document.querySelectorAll('.cart-count-bubble span, .cart-drawer__count').forEach((el) => {
              el.textContent = cart.item_count;
            });
            document.querySelectorAll('.cart-count-bubble').forEach((el) => {
              el.dataset.cartCount = cart.item_count;
              el.style.display = cart.item_count > 0 ? '' : 'none';
            });
            if (window.themeBus && typeof window.themeBus.publish === 'function') {
              window.themeBus.publish('cart:updated', cart);
            }
          })
          .catch(() => {});

        if (window.themeSettings && window.themeSettings.cartType === 'page') {
          window.location.href = cartUrl;
          return;
        }

        if (window.themeCart && typeof window.themeCart.open === 'function') {
          window.themeCart.open();
        } else {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            if (typeof drawer.open === 'function') {
              drawer.open();
            } else {
              drawer.setAttribute('open', '');
              document.body.style.overflow = 'hidden';
            }
          } else {
            window.location.href = cartUrl;
          }
          if (window.themeBus && typeof window.themeBus.publish === 'function') {
            window.themeBus.publish('cart:open');
          }
        }
      })
      .catch((error) => {
        console.error('Add to Cart Network Error:', error);
        this.handleErrorMessage('An error occurred. Please try again.');
      })
      .finally(() => {
        if (this.submitButton) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (!this.errorContainer) {
      this.errorContainer = this.querySelector('.product-form__error-message-wrapper');
    }
    if (!this.errorContainer) return;

    this.errorContainer.textContent = errorMessage || '';
    if (errorMessage) {
      this.errorContainer.removeAttribute('hidden');
      this.errorContainer.setAttribute('aria-live', 'assertive');
      this.errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      this.errorContainer.setAttribute('hidden', 'true');
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

/**
 * Universal Quantity Breaks Tiers Controller
 * Two-way synchronization between tier radios and quantity input steppers,
 * keyboard accessible label/radio selection, dynamic row price updates on variant change,
 * and reliable dynamic checkout ("Buy it now") quantity propagation.
 */
function initQuantityTiers(scope = document) {
  const containers = scope.querySelectorAll ? scope.querySelectorAll('.qty-breaks') : [];
  containers.forEach((container) => {
    if (container._tiersBound) return;
    container._tiersBound = true;

    const sectionId = container.dataset.sectionId || '';
    const form = (sectionId ? document.querySelector(`#product-form-${sectionId}`) : null)
      || container.closest('.product-v2')?.querySelector('product-form form')
      || document.querySelector('product-form form')
      || document.querySelector('form[action*="/cart/add"]');
    const formQtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    const externalQtyInput = document.querySelector('.product-v2__quantity-selector-wrap input[name="quantity"]');
    const tierLabels = container.querySelectorAll('.qty-breaks__item');
    const tierRadios = container.querySelectorAll('input[type="radio"]');

    function syncTierUI(targetQty) {
      let matched = false;
      tierLabels.forEach((label) => {
        const qty = parseInt(label.dataset.tierQty, 10);
        const radio = label.querySelector('input[type="radio"]');
        if (qty === targetQty) {
          label.classList.add('is-selected');
          if (radio) radio.checked = true;
          matched = true;
        } else {
          label.classList.remove('is-selected');
          if (radio) radio.checked = false;
        }
      });

      if (!matched) {
        tierLabels.forEach((label) => label.classList.remove('is-selected'));
        tierRadios.forEach((radio) => radio.checked = false);
      }
    }

    function selectTier(targetQty, updateInputs = true) {
      syncTierUI(targetQty);

      if (updateInputs) {
        if (formQtyInput) {
          formQtyInput.value = targetQty;
          formQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (externalQtyInput && parseInt(externalQtyInput.value, 10) !== targetQty) {
          externalQtyInput.value = targetQty;
          externalQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Radio change listener
    tierRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          const qty = parseInt(radio.value, 10) || 1;
          selectTier(qty, true);
        }
      });
    });

    // Label click listener (support keyboard and pointer click)
    tierLabels.forEach((label) => {
      label.addEventListener('click', (event) => {
        if (event.target.tagName === 'INPUT') return;
        const qty = parseInt(label.dataset.tierQty, 10) || 1;
        const radio = label.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        selectTier(qty, true);
      });
    });

    // Two-way sync: listen to external quantity input
    if (externalQtyInput && !externalQtyInput._tierSyncBound) {
      externalQtyInput._tierSyncBound = true;
      const onExternalChange = () => {
        const qty = parseInt(externalQtyInput.value, 10) || 1;
        if (formQtyInput) formQtyInput.value = qty;
        syncTierUI(qty);
      };
      externalQtyInput.addEventListener('change', onExternalChange);
      externalQtyInput.addEventListener('input', onExternalChange);
    }

    // Safeguard for dynamic checkout ("Buy it now")
    if (form) {
      const dynamicPaymentTerms = form.querySelector('.shopify-payment-terms');
      if (dynamicPaymentTerms && !dynamicPaymentTerms._tierSyncBound) {
        dynamicPaymentTerms._tierSyncBound = true;
        const syncDynamicQuantity = () => {
          const checkedRadio = container.querySelector('input[type="radio"]:checked');
          const activeLabel = container.querySelector('.qty-breaks__item.is-selected');
          let currentQty = 1;
          if (checkedRadio) {
            currentQty = parseInt(checkedRadio.value, 10) || 1;
          } else if (activeLabel) {
            currentQty = parseInt(activeLabel.dataset.tierQty, 10) || 1;
          } else if (externalQtyInput) {
            currentQty = parseInt(externalQtyInput.value, 10) || 1;
          }
          if (formQtyInput) formQtyInput.value = currentQty;
        };
        dynamicPaymentTerms.addEventListener('pointerdown', syncDynamicQuantity, true);
        dynamicPaymentTerms.addEventListener('click', syncDynamicQuantity, true);
      }
    }

    // Dynamic price calculation on variant change
    function updateTierPrices(variant) {
      if (!variant || typeof variant.price === 'undefined') return;
      const moneyFormat = (window.themeSettings && window.themeSettings.moneyFormat) || '₹{{amount}}';
      const formatMoney = (cents) => {
        const val = (cents / 100).toFixed(2);
        const valNoDec = (cents / 100).toFixed(0);
        return moneyFormat
          .replace('{{amount}}', val)
          .replace('{{amount_no_decimals}}', valNoDec)
          .replace('{{amount_with_comma_separator}}', val.replace('.', ','));
      };

      tierLabels.forEach((label) => {
        const qty = parseInt(label.dataset.tierQty, 10) || 1;
        const disc = parseInt(label.dataset.tierDiscount, 10) || 0;
        const factor = (100 - disc) / 100.0;
        const total = Math.round(variant.price * qty * factor);
        const compare = variant.price * qty;

        const compareEl = label.querySelector('.qty-breaks__compare-price');
        const totalEl = label.querySelector('.qty-breaks__total-price');

        if (totalEl) totalEl.textContent = formatMoney(total);
        if (compareEl) {
          if (disc > 0) {
            compareEl.textContent = formatMoney(compare);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
      });
    }

    container.updateTierPrices = updateTierPrices;

    if (window.themeBus && typeof window.themeBus.subscribe === 'function') {
      window.themeBus.subscribe('variant:changed', (data) => {
        if (!data) return;
        if (!sectionId || data.sectionId === sectionId) {
          updateTierPrices(data.variant);
        }
      });
    }

    // Set initial selection
    const initialChecked = container.querySelector('input[type="radio"]:checked') || container.querySelector('.qty-breaks__item.is-selected');
    if (initialChecked) {
      const initQty = parseInt(initialChecked.value || initialChecked.dataset.tierQty, 10) || 1;
      selectTier(initQty, true);
    }
  });
}

window.initQuantityTiers = initQuantityTiers;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initQuantityTiers());
} else {
  initQuantityTiers();
}

// Global Event Delegation fallback for any product form
document.addEventListener('submit', function (event) {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;
  // If wrapped in custom element <product-form>, it is already handled
  if (form.closest('product-form')) return;

  event.preventDefault();
  const submitBtn = form.querySelector('[type="submit"]');
  if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;

  if (submitBtn) {
    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.classList.add('loading');
  }

  const formData = new FormData(form);
  formData.append('sections', 'cart-drawer');

  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

  fetch(addUrl, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: formData
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.status && data.status >= 400) {
        alert(data.description || data.message || 'Unable to add item');
        return;
      }
      if (data.sections && window.themeCart && typeof window.themeCart.render === 'function') {
        window.themeCart.render(data.sections);
      }
      if (window.themeBus && typeof window.themeBus.publish === 'function') {
        window.themeBus.publish('cart:updated', data);
      }
      if (window.themeSettings && window.themeSettings.cartType === 'page') {
        window.location.href = cartUrl;
      } else if (window.themeCart && typeof window.themeCart.open === 'function') {
        window.themeCart.open();
      } else {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          if (typeof drawer.open === 'function') drawer.open();
          else drawer.setAttribute('open', '');
        } else {
          window.location.href = cartUrl;
        }
        if (window.themeBus && typeof window.themeBus.publish === 'function') {
          window.themeBus.publish('cart:open');
        }
      }
    })
    .catch((err) => {
      console.error('Add to Cart delegation error:', err);
    })
    .finally(() => {
      if (submitBtn) {
        submitBtn.removeAttribute('aria-disabled');
        submitBtn.classList.remove('loading');
      }
    });
});

// Re-init on shopify:section:load
document.addEventListener('shopify:section:load', function (event) {
  const root = event.target || document;
  const forms = root.querySelectorAll ? root.querySelectorAll('product-form') : [];
  forms.forEach((pf) => {
    if (typeof pf.init === 'function') pf.init();
  });
  initQuantityTiers(root);
});

// Re-init on shopify:block:select
document.addEventListener('shopify:block:select', function (event) {
  const blockEl = event.target;
  if (!blockEl) return;
  const qtyBreaks = blockEl.closest ? (blockEl.closest('.qty-breaks') || blockEl.querySelector('.qty-breaks')) : null;
  if (qtyBreaks) {
    initQuantityTiers(qtyBreaks.parentElement || document);
  }
});
